import React, { useCallback } from "react";
import styled from "@emotion/styled";
import moment from "moment";
import { MonthlyLocationProjection, ProjectedMonthRevenue } from "../../../models/revenue/BillingConfig";
import {
    color_text_default,
    color_text_light,
    color_colors_ocean,
    color_colors_growth,
    color_colors_decline,
    color_defaults_white,
    color_shades_light,
    color_shades_lightest,
    color_shades_darker,
} from "../../../constants/colors";

interface ProjectionsTableProps {
    orgId: string;
    monthlyProjections: MonthlyLocationProjection[];
    projectedRevenue: ProjectedMonthRevenue[];
    averageRevenuePerLocation: number;
    onUpdateProjection: (orgId: string, month: string, locationCount: number) => void;
    onAddMonth: (orgId: string) => void;
    onAddMonthToStart: (orgId: string) => void;
    onRemoveMonth: (orgId: string, month: string) => void;
    onInitialize: (orgId: string) => void;
}

const ProjectionsTable: React.FC<ProjectionsTableProps> = ({
    orgId,
    monthlyProjections,
    projectedRevenue,
    averageRevenuePerLocation,
    onUpdateProjection,
    onAddMonth,
    onAddMonthToStart,
    onRemoveMonth,
    onInitialize,
}) => {
    const handleLocationCountChange = useCallback(
        (month: string, value: string) => {
            const parsed = parseInt(value, 10);
            if (!isNaN(parsed) && parsed >= 0) {
                onUpdateProjection(orgId, month, parsed);
            }
        },
        [orgId, onUpdateProjection]
    );

    if (monthlyProjections.length === 0) {
        return (
            <EmptyTableState>
                <p>No projections configured for this organization.</p>
                <InitButton onClick={() => onInitialize(orgId)}>
                    Initialize 12-Month Projection
                </InitButton>
            </EmptyTableState>
        );
    }

    const formatMonth = (monthStr: string): string => {
        return moment(monthStr, "YYYY-MM").format("MMM YYYY");
    };

    const getProjectedRevenueForMonth = (month: string): number => {
        const pr = projectedRevenue.find((p) => p.month === month);
        return pr ? pr.projectedRevenue : 0;
    };

    const totalProjectedRevenue = projectedRevenue.reduce(
        (sum, pr) => sum + pr.projectedRevenue,
        0
    );

    return (
        <TableContainer>
            <ScrollWrapper>
                <Table>
                    <thead>
                        <tr>
                            <HeaderCell sticky>Metric</HeaderCell>
                            <HeaderCell>
                                <AddMonthButton onClick={() => onAddMonthToStart(orgId)} title="Add month at beginning">
                                    +
                                </AddMonthButton>
                            </HeaderCell>
                            {monthlyProjections.map((mp, index) => {
                                const isFirst = index === 0;
                                const isLast = index === monthlyProjections.length - 1;
                                const onlyOne = monthlyProjections.length === 1;
                                return (
                                    <HeaderCell key={mp.month}>
                                        <MonthHeader>
                                            {isFirst && !onlyOne && (
                                                <RemoveButton
                                                    onClick={() => onRemoveMonth(orgId, mp.month)}
                                                    title="Remove first month"
                                                >
                                                    x
                                                </RemoveButton>
                                            )}
                                            <span>{formatMonth(mp.month)}</span>
                                            {isLast && (
                                                <RemoveButton
                                                    onClick={() => onRemoveMonth(orgId, mp.month)}
                                                    title="Remove last month"
                                                >
                                                    x
                                                </RemoveButton>
                                            )}
                                        </MonthHeader>
                                    </HeaderCell>
                                );
                            })}
                            <HeaderCell>
                                <AddMonthButton onClick={() => onAddMonth(orgId)} title="Add another month">
                                    +
                                </AddMonthButton>
                            </HeaderCell>
                            <HeaderCell sticky right>Total</HeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <DataCell sticky label>Location Count</DataCell>
                            <DataCell />
                            {monthlyProjections.map((mp) => (
                                <DataCell key={mp.month}>
                                    <LocationInput
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={mp.locationCount}
                                        onChange={(e) =>
                                            handleLocationCountChange(mp.month, e.target.value)
                                        }
                                    />
                                </DataCell>
                            ))}
                            <DataCell />
                            <DataCell sticky right label>-</DataCell>
                        </tr>
                        <tr>
                            <DataCell sticky label>Avg Rev / Location</DataCell>
                            <DataCell />
                            {monthlyProjections.map((mp) => (
                                <DataCell key={mp.month} muted>
                                    {formatCurrency(averageRevenuePerLocation)}
                                </DataCell>
                            ))}
                            <DataCell />
                            <DataCell sticky right muted>
                                {formatCurrency(averageRevenuePerLocation)}
                            </DataCell>
                        </tr>
                        <tr>
                            <DataCell sticky label>Projected Revenue</DataCell>
                            <DataCell />
                            {monthlyProjections.map((mp) => (
                                <DataCell key={mp.month} revenue>
                                    {formatCurrency(getProjectedRevenueForMonth(mp.month))}
                                </DataCell>
                            ))}
                            <DataCell />
                            <DataCell sticky right revenue>
                                {formatCurrency(totalProjectedRevenue)}
                            </DataCell>
                        </tr>
                    </tbody>
                </Table>
            </ScrollWrapper>
        </TableContainer>
    );
};

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

const TableContainer = styled.div`
    margin-top: 8px;
`;

const ScrollWrapper = styled.div`
    overflow-x: auto;
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;
    font-size: 13px;
`;

const HeaderCell = styled.th<{ sticky?: boolean; right?: boolean }>`
    padding: 10px 12px;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: ${color_text_light};
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 2px solid ${color_shades_light};
    background: ${color_defaults_white};
    white-space: nowrap;

    ${(props) =>
        props.sticky &&
        `
        position: sticky;
        ${props.right ? "right: 0;" : "left: 0;"}
        z-index: 2;
        background: ${color_defaults_white};
        border-${props.right ? "left" : "right"}: 2px solid ${color_shades_light};
    `}
`;

const DataCell = styled.td<{
    sticky?: boolean;
    right?: boolean;
    label?: boolean;
    muted?: boolean;
    revenue?: boolean;
}>`
    padding: 8px 12px;
    text-align: center;
    border-bottom: 1px solid ${color_shades_light};
    background: ${color_defaults_white};
    white-space: nowrap;

    ${(props) =>
        props.sticky &&
        `
        position: sticky;
        ${props.right ? "right: 0;" : "left: 0;"}
        z-index: 1;
        background: ${color_defaults_white};
        border-${props.right ? "left" : "right"}: 2px solid ${color_shades_light};
    `}

    ${(props) =>
        props.label &&
        `
        text-align: left;
        font-weight: 600;
        color: ${color_text_default};
    `}

    ${(props) =>
        props.muted &&
        `
        color: ${color_text_light};
        font-size: 12px;
    `}

    ${(props) =>
        props.revenue &&
        `
        font-weight: 600;
        color: ${color_colors_growth};
    `}
`;

const MonthHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: ${color_colors_decline};
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    padding: 0 4px;
    line-height: 1;
    opacity: 0.5;

    &:hover {
        opacity: 1;
    }
`;

const AddMonthButton = styled.button`
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    border: none;
    border-radius: 4px;
    width: 28px;
    height: 28px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        opacity: 0.9;
    }
`;

const LocationInput = styled.input`
    width: 70px;
    padding: 4px 8px;
    border: 1px solid ${color_shades_light};
    border-radius: 4px;
    font-size: 13px;
    text-align: center;
    color: ${color_text_default};

    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    -moz-appearance: textfield;
`;

const EmptyTableState = styled.div`
    text-align: center;
    padding: 24px;
    color: ${color_text_light};
    font-size: 14px;

    p {
        margin: 0 0 12px 0;
    }
`;

const InitButton = styled.button`
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;

    &:hover {
        opacity: 0.9;
    }
`;

export default React.memo(ProjectionsTable);
